import type { PackageId, PackageOption } from '@detachly/contracts'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, Card, EmptyState, ErrorPanel, Skeleton } from '../components/ui'
import { useResource } from '../hooks/useResource'
import { useApi } from '../lib/api-context'
import { navigateTo } from '../lib/browser'
import { describeError } from '../lib/errors'
import { formatPrice } from '../lib/format'

/** Plural without the "(s)" — the picker is read once and should read like a sentence. */
function count(value: number, noun: string): string {
  return `${value} ${noun}${value === 1 ? '' : 's'}`
}

export function PackagePickerPage() {
  const { projectId = '' } = useParams()
  const api = useApi()

  const project = useResource(() => api.getProject(projectId), [api, projectId])
  const packages = useResource(() => api.recommendPackages(projectId), [api, projectId])
  const owned = useResource(() => api.listEntitlements(projectId), [api, projectId])

  const [buying, setBuying] = useState<PackageId | null>(null)
  const [buyError, setBuyError] = useState<unknown>(null)

  const buy = async (packageId: PackageId) => {
    setBuying(packageId)
    setBuyError(null)
    try {
      const { checkoutUrl } = await api.startCheckout(projectId, packageId)
      // Payment happens on the provider's page; the app is left behind on purpose.
      navigateTo(checkoutUrl)
    } catch (error: unknown) {
      setBuyError(error)
      setBuying(null)
    }
  }

  const hub = `/app/projects/${projectId}`

  const head = (
    <div className="page-head">
      <div className="page-head__text">
        <h1>Choose a package</h1>
        <p className="muted">
          {project.data ? `${project.data.name} · ` : ''}one-time, for this project only
        </p>
      </div>
      <Link to={hub} className="button button--secondary">
        Back to project
      </Link>
    </div>
  )

  if (packages.loading) {
    return (
      <>
        {head}
        <Skeleton rows={4} />
      </>
    )
  }

  // The recommendation is read off a scan, so before the first scan there is nothing to say.
  if (packages.error) {
    const { code } = describeError(packages.error)
    return (
      <>
        {head}
        {code === 'PRECONDITION_FAILED' ? (
          <EmptyState
            title="Nothing scanned yet"
            description="Packages are sized from what a scan finds: how many routes a rebuild would have to produce, and how many collections feed them."
            action={
              <Link to={hub} className="button">
                Scan this project
              </Link>
            }
          />
        ) : (
          <ErrorPanel error={packages.error} onRetry={packages.reload} />
        )}
      </>
    )
  }

  if (!packages.data) return null

  const { signals, recommended, options } = packages.data
  const remaining = owned.data?.aiPagesRemaining ?? 0

  return (
    <>
      {head}

      <div className="stack">
        <p className="muted">
          The last scan found {count(signals.pageCount, 'route')} and{' '}
          {count(signals.collectionCount, 'collection')}. Every route you do not spend an AI page on
          still ships as scaffold with your CMS data.
        </p>

        {remaining > 0 ? (
          <div className="notice">
            <p className="notice__title">
              This project already has {count(remaining, 'AI page')} left.
            </p>
            <p>Buying again adds to it — packages stack, and each one lasts 12 months.</p>
          </div>
        ) : null}

        <div className="notice">
          <p className="notice__title">What an AI page buys you.</p>
          <p>
            One page is one rebuild of your Home page as plain React, installed and built here
            before you can download it — if it does not compile, the page goes back to your package.
            Every other route still ships as scaffold with your CMS data, free.
          </p>
        </div>

        {buyError ? <ErrorPanel error={buyError} /> : null}

        {recommended === 'contact' ? (
          <div className="notice">
            <p className="notice__title">This site is bigger than any self-serve package covers.</p>
            <p>
              {count(signals.pageCount, 'route')} is past the {options.at(-1)?.name} limit, so none
              of the packages below would be honest about what it rebuilds. Content Pack and
              Scaffold are free and already cover every route.
            </p>
          </div>
        ) : null}

        <div className="grid">
          {options.map((option) => (
            <PackageCard
              key={option.packageId}
              option={option}
              recommended={option.packageId === recommended}
              busy={buying !== null}
              buying={buying === option.packageId}
              onBuy={() => void buy(option.packageId)}
            />
          ))}
        </div>

        <p className="subtle">
          Prices are in US dollars, charged once, per project. Tax is added by the payment provider
          where it applies.
        </p>
      </div>
    </>
  )
}

function PackageCard({
  option,
  recommended,
  busy,
  buying,
  onBuy,
}: {
  option: PackageOption
  recommended: boolean
  busy: boolean
  buying: boolean
  onBuy: () => void
}) {
  return (
    <Card>
      <div className="card__title">
        <h2>{option.name}</h2>
        {recommended ? <span className="badge badge--success">Recommended</span> : null}
      </div>

      <p className="price">{formatPrice(option.listPriceCents, option.currency)}</p>

      <ul className="help-list">
        <li>AI rebuilds up to {count(option.includes.aiPages, 'key page')}, starting with Home</li>
        <li>
          {option.includes.retries === 1 ? '1 retry' : `${option.includes.retries} retries`}{' '}
          included
        </li>
        <li>Every other route: scaffold with your CMS data</li>
        <li>Content Pack and Scaffold stay free</li>
      </ul>

      <div className="row" style={{ marginTop: 16 }}>
        <Button onClick={onBuy} disabled={!option.eligible || busy}>
          {buying ? 'Opening checkout…' : `Choose ${option.name}`}
        </Button>
      </div>

      {option.reason ? (
        <p className="subtle" style={{ marginTop: 8 }}>
          {option.reason}
        </p>
      ) : null}
    </Card>
  )
}
